import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import { FilterHelper } from './filter.helper';
import { FilterOptionsProps } from './filter.model';
import { FilterStore } from './filter.store';

/**
 * Service for managing filter translations and applying them to filter UI elements.
 *
 * This service handles translation of filter options, labels, and UI text using
 * the app's translation configuration. It supports nested translation keys,
 * template variable replacement, and automatic XSS sanitization. It can be
 * extended by Technical Support teams to customize translation behavior for
 * specific shops or languages.
 *
 * @remarks
 * The service works with FilterStore to apply translations to both filter options
 * and selected filters. It generates CSS selectors to locate and update filter
 * UI elements dynamically. Translation keys support dot notation for nested objects.
 *
 * @example
 * Extend to add custom translation fallback logic:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
 *   return class CustomFilterTranslationService extends FilterTranslationService {
 *     translateByKey(key, defaultValue) {
 *       // Try primary language
 *       let translated = super.translateByKey(key, defaultValue);
 *       // Fallback to secondary language if not found
 *       if (translated === defaultValue) {
 *         translated = this.getSecondaryLanguageTranslation(key, defaultValue);
 *       }
 *       return translated;
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterTranslationService {
  private TOGGLE_SELECTOR_SUFFIX = '-toggle';
  constructor(
    private appService: AppService,
    private filterStore: FilterStore,
    private filterHelper: FilterHelper
  ) {}

  /**
   * Updates the text content of an HTML element while preserving child elements.
   *
   * This method finds and updates only text nodes within an element, leaving
   * child HTML elements intact. If no text node exists, it creates one as the
   * first child. Also updates aria-label attributes for accessibility.
   *
   * @param el - The HTML element to update, or null (no-op if null)
   * @param newText - The new text content to set. Should be pre-sanitized if from user input.
   *
   * @remarks
   * The method sanitizes aria-label attributes by prepending "Filter by: " to
   * prevent XSS attacks through attribute injection.
   *
   * @example
   * Basic usage:
   * ```typescript
   * const element = document.querySelector('#filter-label');
   * this.setElementText(element, 'Color');
   * ```
   *
   * @example
   * Extend to add custom element handling:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     setElementText(el, newText) {
   *       if (el?.classList.contains('custom-filter')) {
   *         el.setAttribute('data-original-text', newText);
   *         el.textContent = `[Custom] ${newText}`;
   *         return;
   *       }
   *       super.setElementText(el, newText);
   *     }
   *   };
   * });
   * ```
   */
  setElementText(el: HTMLElement | null, newText: string): void {
    if (!el) return;

    // Check if element has a semantic heading (H3) child for ADA compliance
    const headingElement = el.querySelector('.boost-sd__filter-option-heading');
    if (headingElement) {
      // Update the heading content instead of creating a text node
      headingElement.textContent = newText;
      // Also update aria-label for accessibility
      if (el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', `Filter by: ${newText}`);
      }
      return;
    }

    const textNode = Array.from(el.childNodes).find(
      (n) => n.nodeType === Node.TEXT_NODE && n.textContent?.trim() !== ''
    );

    if (textNode) {
      textNode.textContent = newText;
    } else {
      // Also sanitize aria-label attribute to prevent XSS
      if (el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', `Filter by: ${newText}`);
      }
      el.insertBefore(document.createTextNode(newText), el.firstChild);
    }
  }

  /**
   * Replaces template variables in a string with values from an object.
   *
   * Finds all {{key}} patterns in the string and replaces them with corresponding
   * values from the provided object. Whitespace around keys is trimmed. Unknown
   * keys are left unchanged in their original {{key}} format.
   *
   * @param str - The template string containing {{variable}} patterns to replace
   * @param obj - Object with key-value pairs for variable replacement
   * @returns The string with all known variables replaced, unknown variables unchanged
   *
   * @example
   * Basic usage:
   * ```typescript
   * const result = this.translateWithComponent(
   *   'Showing {{count}} of {{total}} items',
   *   { count: 10, total: 50 }
   * );
   * // Returns: "Showing 10 of 50 items"
   * ```
   *
   * @example
   * Extend to add custom variable formatters:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     translateWithComponent(str, obj) {
   *       const enhanced = { ...obj };
   *       // Add currency formatting for price variables
   *       Object.keys(enhanced).forEach(key => {
   *         if (key.includes('price') && typeof enhanced[key] === 'number') {
   *           enhanced[key] = this.formatCurrency(enhanced[key]);
   *         }
   *       });
   *       return super.translateWithComponent(str, enhanced);
   *     }
   *   };
   * });
   * ```
   */
  translateWithComponent(str: string, obj: any) {
    const regex = /\{\{(.*?)\}\}/g;

    return str.replace(regex, (match, key) => {
      // Check if the key exists in the object
      key = key?.trim();
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        return obj[key];
      } else {
        // Key not found, return the original string inside {{ }}
        return match;
      }
    });
  }

  /**
   * Retrieves a translated string by its key from the app's translation configuration.
   *
   * Supports nested keys using dot notation (e.g., 'filter.title.color'). Returns
   * the default value if the key is not found in translations or is invalid.
   *
   * @param key - The translation key, supports dot notation for nested objects.
   * Empty string returns the default value.
   * @param defaultValue - Fallback value returned if translation key doesn't exist.
   * Defaults to empty string.
   * @returns The translated string if found, otherwise the default value
   *
   * @example
   * Basic usage:
   * ```typescript
   * const title = this.translateByKey('filter.title', 'Filters');
   * const nested = this.translateByKey('filter.options.color.label', 'Color');
   * ```
   *
   * @example
   * Extend to add translation caching:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     constructor(appService, filterStore, filterHelper) {
   *       super(appService, filterStore, filterHelper);
   *       this.translationCache = new Map();
   *     }
   *
   *     translateByKey(key, defaultValue) {
   *       const cacheKey = `trans_${key}`;
   *       if (this.translationCache.has(cacheKey)) {
   *         return this.translationCache.get(cacheKey);
   *       }
   *       const result = super.translateByKey(key, defaultValue);
   *       this.translationCache.set(cacheKey, result);
   *       return result;
   *     }
   *   };
   * });
   * ```
   */
  translateByKey(key: string = '', defaultValue: string = ''): string {
    return this.getNestedTranslation(key, this.appService.translation || {}, defaultValue);
  }

  /**
   * Traverses a nested object structure to retrieve a translation value.
   *
   * Splits the key by dots and traverses the translations object step by step.
   * Returns the default label if any part of the path doesn't exist or if the
   * final value is not a string.
   *
   * @param key - Dot-separated key path (e.g., 'filter.options.color')
   * @param translations - The translations object to search within
   * @param defaultLabel - Fallback value if key path doesn't resolve to a string
   * @returns The translated string if found, otherwise the default label
   *
   * @remarks
   * This is a utility method used by `translateByKey`. It safely handles missing
   * keys and invalid object structures by returning the default label.
   *
   * @example
   * ```typescript
   * const result = this.getNestedTranslation(
   *   'filter.color',
   *   { filter: { color: 'Couleur' } },
   *   'Color'
   * );
   * // Returns: "Couleur"
   * ```
   */
  getNestedTranslation(
    key: string,
    translations: Record<string, any>,
    defaultLabel: string
  ): string {
    if (!translations || typeof translations !== 'object' || !key) {
      return defaultLabel;
    }

    const keys = key.split('.');
    let current = translations;

    for (const k of keys) {
      if (typeof current === 'object' && current !== null && k in current) {
        current = current[k];
      } else {
        return defaultLabel;
      }
    }

    return typeof current === 'string' ? current : defaultLabel;
  }

  /**
   * Translates and updates the text content of an HTML element.
   *
   * Convenience method that combines `translateByKey` and `setElementText`.
   * Looks up the translation for the given key and applies it to the element.
   *
   * @param element - The HTML element to update with translated text
   * @param key - The translation key, supports dot notation
   * @param defaultValue - Fallback text if translation key not found
   *
   * @example
   * ```typescript
   * const titleElement = document.querySelector('.filter-title');
   * this.translate(titleElement, 'filter.title', 'Filters');
   * ```
   */
  translate(element: HTMLElement, key: string = '', defaultValue: string = '') {
    const translated = this.translateByKey(key, defaultValue);
    this.setElementText(element as HTMLElement, translated);
  }

  /**
   * Generates a CSS selector for a filter toggle element.
   *
   * Sanitizes the ID by replacing colons with underscores and properly escapes
   * special characters for use in CSS selectors. Appends the configured suffix
   * (default: '-toggle') to create the final selector.
   *
   * @param id - The filter option ID to create a selector for
   * @param suffix - Optional suffix to append. Defaults to TOGGLE_SELECTOR_SUFFIX ('-toggle')
   * @returns CSS ID selector string in format '#escaped-id-suffix'
   *
   * @remarks
   * Uses CSS.escape() for proper escaping of special characters in IDs.
   * Colons are replaced before escaping to handle namespaced IDs.
   *
   * @example
   * Basic usage:
   * ```typescript
   * const selector = this.getToggleSelector('color:red');
   * // Returns: "#color_red-toggle"
   * const element = document.querySelector(selector);
   * ```
   *
   * @example
   * Extend to customize selector format:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     getToggleSelector(id, suffix = '-filter-btn') {
   *       // Use custom suffix for shop's HTML structure
   *       return super.getToggleSelector(id, suffix);
   *     }
   *   };
   * });
   * ```
   */
  getToggleSelector(id: string, suffix = this.TOGGLE_SELECTOR_SUFFIX): string {
    const sanitizedId = id.replace(/:/g, '_');
    const escapedId = CSS.escape(sanitizedId);
    return `#${escapedId}${suffix}`;
  }

  /**
   * Builds a standardized translation key for a filter option.
   *
   * Constructs a key in the format 'filterOption|{filterId}|{optionKey}' used
   * to look up translations in the filterOptions translation object.
   *
   * @param filterId - The unique identifier of the filter
   * @param optionKey - The option key within the filter
   * @returns Translation key string in format 'filterOption|filterId|optionKey'
   *
   * @example
   * ```typescript
   * const key = this.buildTranslationKey('color', 'red');
   * // Returns: "filterOption|color|red"
   * ```
   *
   * @example
   * Extend to use custom key format:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     buildTranslationKey(filterId, optionKey) {
   *       // Use shop's custom key format
   *       return `custom.filter.${filterId}.${optionKey}`;
   *     }
   *   };
   * });
   * ```
   */
  buildTranslationKey(filterId: string, optionKey: string): string {
    return `filterOption|${filterId}|${optionKey}`;
  }

  /**
   * Retrieves a translated filter option label from the app's translation configuration.
   *
   * Builds a translation key in the format 'filterOption|{filterId}|{optionKey}' and
   * looks it up in the filterOptions translation object. Falls back to the provided
   * default value or the original key if translation is not found.
   *
   * @param filterState - The filter state object containing the filterId property
   * @param key - The option key to translate (e.g., 'red', 'large', 'available')
   * @param defaultValue - Optional fallback value. If not provided, returns the key itself.
   * @returns The translated label if found, otherwise the default value or key
   *
   * @remarks
   * This is a critical extension point for customizing how filter options are translated.
   * Common customizations include adding fallback languages, custom formatting,
   * shop-specific label mappings, or dynamic translation logic based on context.
   *
   * @example
   * Basic usage:
   * ```typescript
   * const translatedLabel = this.getTranslatedFilterOption(
   *   filterState,
   *   'red',
   *   'Red'
   * );
   * ```
   *
   * @example
   * Extend to add fallback language support:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     getTranslatedFilterOption(filterState, key, defaultValue) {
   *       // Try primary language
   *       let translated = super.getTranslatedFilterOption(filterState, key, defaultValue);
   *
   *       // Fallback to secondary language if translation not found
   *       if (translated === (defaultValue ?? key)) {
   *         const secondaryKey = this.buildTranslationKey(
   *           filterState.filterId,
   *           `${key}_fr`
   *         );
   *         translated = this.lookupSecondaryTranslation(secondaryKey) ?? translated;
   *       }
   *
   *       return translated;
   *     }
   *   };
   * });
   * ```
   *
   * @example
   * Extend to add translation logging:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     getTranslatedFilterOption(filterState, key, defaultValue) {
   *       const result = super.getTranslatedFilterOption(filterState, key, defaultValue);
   *
   *       // Log missing translations for shop owner review
   *       if (result === (defaultValue ?? key)) {
   *         console.warn(
   *           `Missing translation: ${filterState.filterId}.${key}`,
   *           { filterState, key, defaultValue }
   *         );
   *       }
   *
   *       return result;
   *     }
   *   };
   * });
   * ```
   */
  getTranslatedFilterOption(
    filterState: FilterOptionsProps,
    key: string,
    defaultValue?: string
  ): string {
    const filterId = filterState.filterId;

    if (!filterId || !key) {
      return defaultValue ?? key;
    }

    const translations = this.appService.TAEAppConfig.translation;
    const filterOptions = translations.filterOptions ?? {};

    if (filterOptions === null || typeof filterOptions !== 'object') {
      return defaultValue ?? key;
    }

    // Use built key for exact match (less error-prone than find + split)
    const translationKey = this.buildTranslationKey(filterId, key);
    if (translationKey in filterOptions && typeof filterOptions[translationKey] === 'string') {
      return filterOptions[translationKey];
    }

    return defaultValue ?? key;
  }

  /**
   * Applies translations to all filter options in the current filter state.
   *
   * Iterates through all filter options in the filter state, retrieves their
   * translated labels, sanitizes them for XSS prevention (unless disabled in config),
   * and updates the corresponding DOM elements. Finally updates the filter state
   * with the translated labels.
   *
   * @remarks
   * This method performs DOM queries for each filter option using generated selectors.
   * XSS sanitization is applied via FilterHelper unless explicitly disabled in
   * TAEAppConfig.xss.filter.disabled. The method updates both the DOM elements
   * and the filter state in the store.
   *
   * @example
   * Basic usage:
   * ```typescript
   * // Called after filter options are loaded
   * this.applyTranslationsToFilterOptions();
   * ```
   *
   * @example
   * Extend to add performance optimization:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     applyTranslationsToFilterOptions() {
   *       const startTime = performance.now();
   *       super.applyTranslationsToFilterOptions();
   *       const duration = performance.now() - startTime;
   *
   *       if (duration > 100) {
   *         console.warn(`Slow translation application: ${duration}ms`);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  applyTranslationsToFilterOptions() {
    const filterState = this.filterStore.getState('filter');

    for (const option of filterState.options || []) {
      if (typeof option.filterOptionId !== 'string' || !option.label) {
        // Guard missing label
        continue;
      }
      const { filterOptionId, label: defaultLabel } = option;
      const isXssDisabled = (this.appService.TAEAppConfig as any).xss?.filter?.disabled;
      const translatedLabel = this.getTranslatedFilterOption(
        filterState,
        filterOptionId,
        defaultLabel
      );

      // Sanitize the final label (covers both translation and default fallback)
      const finalLabel = isXssDisabled
        ? translatedLabel
        : this.filterHelper.sanitizeFilterLabel(translatedLabel);

      const elements = document.querySelectorAll(this.getToggleSelector(filterOptionId));
      elements.forEach((element) => {
        if (element instanceof HTMLElement) {
          // Narrow type for safety
          this.setElementText(element, finalLabel);
        }
      });
    }
    this.filterStore.setState({ filter: filterState });
  }

  /**
   * Applies translations to the "refine by" selected filters display.
   *
   * Updates the labels of selected filters with their translated values. This is
   * used to display translated filter names in the active filters / "refine by"
   * section of the UI.
   *
   * @param selectedFilters - Array of selected filter objects to translate.
   * Each object should have a `data` property with `key` and `label` fields.
   *
   * @remarks
   * This method modifies the provided array in place by updating the `data.label`
   * property of each filter. Only processes filters with valid data.key and
   * data.label string properties.
   *
   * @example
   * Basic usage:
   * ```typescript
   * const selectedFilters = [
   *   { data: { key: 'red', label: 'Red' } },
   *   { data: { key: 'large', label: 'Large' } }
   * ];
   * this.applyTranslationsToRefineBy(selectedFilters);
   * ```
   *
   * @example
   * Extend to add custom formatting:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterTranslationService', (FilterTranslationService) => {
   *   return class CustomFilterTranslationService extends FilterTranslationService {
   *     applyTranslationsToRefineBy(selectedFilters) {
   *       super.applyTranslationsToRefineBy(selectedFilters);
   *
   *       // Add custom prefix for VIP customers
   *       if (this.isVipCustomer()) {
   *         selectedFilters.forEach(filter => {
   *           if (filter.data?.label) {
   *             filter.data.label = `⭐ ${filter.data.label}`;
   *           }
   *         });
   *       }
   *     }
   *   };
   * });
   * ```
   */
  applyTranslationsToRefineBy(selectedFilters: any[]): void {
    if (!Array.isArray(selectedFilters)) {
      return;
    }

    const filterState = this.filterStore.getState('filter');

    for (const selectedFilter of selectedFilters) {
      if (selectedFilter.data?.key && typeof selectedFilter.data.label === 'string') {
        selectedFilter.data.label = this.getTranslatedFilterOption(
          filterState,
          selectedFilter.data.key,
          selectedFilter.data.label
        );
      }
    }
  }
}
